import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Form } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { setShowAssetSelection } from 'packages/store/creativeEditor/creativeEditor.actions';
import { showAssetSelectionSelector } from 'packages/store/creativeEditor/creativeEditor.selectors';
import { getElementPropertiesFromSource } from '~/common/editor/helpers';
import BrowseButton from '../../../FormComponents/BrowseButton';
import { ContentType } from '../../../FormComponents/BrowseButton/types';
import { ImageElement, MultiPageImageModel, MultiPageVideoModel } from '@bynder-studio/render-core';

type Props = {
    creativeModel: MultiPageVideoModel | MultiPageImageModel;
    selectedElement: ImageElement;
    disabled?: boolean;
};

const BrowseImage = ({ creativeModel, selectedElement, disabled }: Props) => {
    const { translate } = useTranslate();
    const showAssetSelection: boolean = useSelector(showAssetSelectionSelector);
    const { locked, duration, dimension, src, srcId, fileName, id: elementId } = selectedElement;

    const handleSelectSource = useCallback(
        (selectedSource) => {
            const resetDefault = srcId !== selectedSource.id;

            const params = getElementPropertiesFromSource(selectedElement, 'IMAGE', selectedSource);

            if (resetDefault) {
                // Reset to default values
                if (params.contentTransform) {
                    const { width: naturalWidth, height: naturalHeight } = params.naturalDimension;
                    const { width, height } = dimension;
                    const scale = Number(Math.min(width / naturalWidth, height / naturalHeight, 1).toFixed(2));

                    params.contentTransform.scale = scale;
                    params.contentTransform.horizontalOffset = Math.round(width / 2 - (naturalWidth * scale) / 2);
                    params.contentTransform.verticalOffset = Math.round(height / 2 - (naturalHeight * scale) / 2);
                } else {
                    const { type } = selectedElement.contentTransform;
                    params.contentTransform = {
                        verticalAlignment: 0,
                        horizontalAlignment: 0,
                        type,
                    };
                }
            }

            creativeModel.updateElement(elementId, params);
        },
        [creativeModel, dimension, elementId, selectedElement, srcId],
    );

    return (
        <Form.Group>
            {src && <Form.Label>{translate('editor.sidebar.shots.image.tabs.content')}</Form.Label>}
            <BrowseButton
                type={ContentType.IMAGE}
                frameRate={0}
                fileName={fileName}
                selectedObject={{ element: selectedElement, duration }}
                disabled={locked || disabled}
                handleSelectSource={handleSelectSource}
                show={showAssetSelection}
                setShowAssetSelection={setShowAssetSelection}
                mediaSrc={src}
                testId={`design_scene_image_asset`}
                resizableRange
                isEditorPage
            />
        </Form.Group>
    );
};

export default BrowseImage;
